// libraries
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { apiConfigHelper, locHrefUtil, MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import {
    CUDL_PROVIDER_CONFIG,
    PushToFIStatus,
    ROUTE_ONE_PROVIDER_CONFIG,
    type PushToFIProvider,
    type StateTree
} from '@makemydeal/dr-dash-types';

// consts/enums
import {
    CONFIRM_PUSH_TO_FI,
    PUSH_TO_FI_DEAL_REF_ID_TIMEOUT_EVENT,
    PUSH_TO_FI_FAILURE,
    PUSH_TO_FI_OFFER_SAVE_FAILURE_EVENT,
    UPDATE_PUSH_TO_FI
} from '../actionTypes/pushToFIActionTypes';
import {
    PUSH_TO_FI_DEAL_REF_ID_TIMEOUT_MS,
    PUSH_TO_FI_FLOW,
    PUSH_TO_FI_INITIATED_PAGE_ACTION,
    PUSH_TO_FI_RESULT_PAGE_ACTION
} from '../constants';

// actions
import {
    pushToFIDealRefIdTimedOut,
    pushToFIOfferSaveFailed,
    pushToFIRSAA,
    type PushToFIPayloadOverrides
} from '../actions/pushToFIActionCreators';
import { saveOffer } from '../actions/offerActionCreators';

// selectors
import { getPushToFIStatus } from '../selectors/pushToFISelectors';
import { getDealRefIdFS, getDealXgId } from '../selectors/offerRedux';
import { dealerSelectors } from '@makemydeal/dr-shared-store';

// interfaces/types
import { type DashMiddleware, type DashStore, type DashNext } from '@makemydeal/dr-shared-store';
import {
    LINKS_FAILURE,
    LINKS_SUCCESS,
    OFFER_SAVE_FAILURE,
    OFFER_SAVE_NOCHANGE_SUCCESS,
    OFFER_SAVE_SUCCESS,
    SOFT_SAVE_FAILURE,
    SOFT_SAVE_SUCCESS,
    UPDATE_DEAL_REF_ID
} from '../actionTypes/offerActionTypes';

const buildSourceForProvider = (providerId: string): string => `${PUSH_TO_FI_FLOW}:${providerId}`;

// Normalize frontend environment names to match bff environment names
const getEnvironmentName = (): string => {
    const feToBffEnvironment: Record<string, string> = { prod: 'production', darkprod: 'dark' };
    const name = apiConfigHelper
        .buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHrefUtil.getLocHref(), '/pushToFI')
        .environmentName.toLowerCase();
    return feToBffEnvironment[name] ?? name;
};

// Observability: common attributes shared by both Push-to-FI page actions. dealRefId is the
// durable correlation key (the only id that survives the async round trip); correlationId and
// dealXgId are included when available. New Relic drops attributes with undefined values.
const buildPushToFIPageActionAttrs = (state: StateTree, providerId: string) => {
    const slice = getPushToFIStatus(state, providerId);
    return {
        environment: getEnvironmentName(),
        targetPlatform: providerId,
        dealRefId: getDealRefIdFS(state),
        dealXgId: getDealXgId(state),
        dealerId: dealerSelectors.getDealerId(state),
        correlationId: slice?.correlationId
    };
};

// Maps the three terminal Push-to-FI actions to a binary outcome plus, on failure, the cause.
// offerSaveFailure is a failure reason, not a peer of success/failure.
const resolveResult = (action: AnyFSA): { outcome: 'success' | 'failure'; failureReason?: string } => {
    switch (action.type) {
        case UPDATE_PUSH_TO_FI:
            return action.payload?.ok ? { outcome: 'success' } : { outcome: 'failure', failureReason: 'push_rejected' };
        case PUSH_TO_FI_OFFER_SAVE_FAILURE_EVENT:
            return { outcome: 'failure', failureReason: 'offer_save_failed' };
        case PUSH_TO_FI_DEAL_REF_ID_TIMEOUT_EVENT:
            return { outcome: 'failure', failureReason: 'deal_ref_id_timeout' };
        case PUSH_TO_FI_FAILURE:
        default:
            return { outcome: 'failure', failureReason: 'bff_error' };
    }
};

const getTerminalProviderId = (action: AnyFSA): string | undefined => action.payload?.providerId ?? action.meta?.providerId;

const extractProviderIdFromSource = (source: unknown): string | null => {
    if (typeof source !== 'string') return null;
    const prefix = `${PUSH_TO_FI_FLOW}:`;
    if (!source.startsWith(prefix)) return null;
    return source.slice(prefix.length);
};

const getProviderById = (providerId: string): PushToFIProvider | null => {
    if (providerId === CUDL_PROVIDER_CONFIG.targetPlatformId) return CUDL_PROVIDER_CONFIG;
    if (providerId === ROUTE_ONE_PROVIDER_CONFIG.targetPlatformId) return ROUTE_ONE_PROVIDER_CONFIG;
    return null;
};

type PendingRefIdWait = {
    timeoutId: ReturnType<typeof setTimeout>;
    provider: PushToFIProvider;
    overrides: PushToFIPayloadOverrides;
};
const pendingRefIdWaits = new Map<string, PendingRefIdWait>();

const clearRefIdWait = (providerId: string) => {
    const pending = pendingRefIdWaits.get(providerId);
    if (pending) {
        clearTimeout(pending.timeoutId);
        pendingRefIdWaits.delete(providerId);
    }
};

/**
 * Start a timer to wait for the deal ref id to become available.
 * If the deal doesn't have a deal ref id yet then wait a few seconds to see if we get one from a websocket message
 */
const beginRefIdWait = (store: DashStore, provider: PushToFIProvider, overrides: PushToFIPayloadOverrides) => {
    const providerId = provider.targetPlatformId;
    clearRefIdWait(providerId);

    const timeoutId = setTimeout(() => {
        pendingRefIdWaits.delete(providerId);
        if (getPushToFIStatus(store.getState(), providerId)?.status !== PushToFIStatus.Waiting) return;
        store.dispatch(pushToFIDealRefIdTimedOut(providerId));
    }, PUSH_TO_FI_DEAL_REF_ID_TIMEOUT_MS);

    pendingRefIdWaits.set(providerId, { timeoutId, provider, overrides });
};

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    next(action);

    switch (action.type) {
        case CONFIRM_PUSH_TO_FI: {
            const providerId = action.payload?.providerId;
            if (!providerId) break;
            // Start the push first; emit telemetry afterwards so observability can never block the flow.
            store.dispatch(
                saveOffer({
                    originalAction: CONFIRM_PUSH_TO_FI,
                    source: buildSourceForProvider(providerId)
                })
            );
            logNewRelicPageAction(PUSH_TO_FI_INITIATED_PAGE_ACTION, buildPushToFIPageActionAttrs(store.getState(), providerId));
            break;
        }
        case UPDATE_PUSH_TO_FI:
        case PUSH_TO_FI_OFFER_SAVE_FAILURE_EVENT:
        case PUSH_TO_FI_DEAL_REF_ID_TIMEOUT_EVENT:
        case PUSH_TO_FI_FAILURE: {
            const providerId = getTerminalProviderId(action);
            if (!providerId) break;
            const state = store.getState();
            const slice = getPushToFIStatus(state, providerId);
            const { outcome, failureReason } = resolveResult(action);
            logNewRelicPageAction(PUSH_TO_FI_RESULT_PAGE_ACTION, {
                ...buildPushToFIPageActionAttrs(state, providerId),
                outcome,
                failureReason,
                elapsedMs: slice?.initiatedAt ? Date.now() - slice.initiatedAt : undefined
            });
            break;
        }
        case LINKS_SUCCESS:
        case OFFER_SAVE_NOCHANGE_SUCCESS:
        case SOFT_SAVE_SUCCESS:
        case OFFER_SAVE_SUCCESS: {
            const providerId = extractProviderIdFromSource(action.meta?.source);
            if (!providerId) break;
            const provider = getProviderById(providerId);
            if (!provider) break;
            const currentState = store.getState();
            const payloadOverrides = {
                dealXgId: action.payload?.dealXgId,
                dealXgVersion: action.payload?.dealXgVersion
            };
            if (!getDealRefIdFS(currentState)) {
                beginRefIdWait(store, provider, payloadOverrides);
                break;
            }
            store.dispatch(pushToFIRSAA(MANAGER_VIEW_APP_PREFIX, currentState, provider, payloadOverrides));
            break;
        }
        case LINKS_FAILURE:
        case SOFT_SAVE_FAILURE:
        case OFFER_SAVE_FAILURE: {
            const providerId = extractProviderIdFromSource(action.meta?.source);
            if (!providerId) break;
            clearRefIdWait(providerId);
            store.dispatch(pushToFIOfferSaveFailed(providerId));
            break;
        }
        case UPDATE_DEAL_REF_ID: {
            if (pendingRefIdWaits.size === 0) break;
            if (!getDealRefIdFS(store.getState())) break; // arrived empty/cleared -> keep waiting
            for (const [providerId, pending] of Array.from(pendingRefIdWaits.entries())) {
                clearRefIdWait(providerId);
                store.dispatch(pushToFIRSAA(MANAGER_VIEW_APP_PREFIX, store.getState(), pending.provider, pending.overrides));
            }
            break;
        }
        default:
            break;
    }
};
